import {
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Box,
  HStack,
  Text,
} from "@chakra-ui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import FormLabelWithInfoTooltip from "../Widgets/FormLabelWithInfoTooltip";

const ConfigureMultipliersModal = ({
  isOpen,
  onClose,
  fetchAgencyBillingPlanList,
  tableData = [],
  itemToUpdate = null,
  onAgencyUpdated,
}) => {
  const baseUrl = getHostDomain();
  const [isLoading, setIsLoading] = useState(false);
  const finalRef = useRef(null);
  const agency = useContext(AgencyContext);
  const { agencyData, textColor, buttonColorScheme } = agency || {};

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      transcriber_cost: 1.0,
      llm_cost: 1.0,
      voice_cost: 1.0,
      platform_cost: 1.0,
    },
  });

  const populateForm = (source = {}) => {
    const multipliersFromItem =
      itemToUpdate?.subaccount_wallet?.multipliers || itemToUpdate?.multipliers;

    const multipliers =
      multipliersFromItem ||
      source?.subaccount_wallet?.multipliers ||
      source?.billing_plan?.subaccount_wallet?.multipliers ||
      source?.multipliers;

    if (multipliers) {
      if (multipliers.transcriber_cost !== undefined)
        setValue("transcriber_cost", multipliers.transcriber_cost);
      if (multipliers.llm_cost !== undefined)
        setValue("llm_cost", multipliers.llm_cost);
      if (multipliers.voice_cost !== undefined)
        setValue("voice_cost", multipliers.voice_cost);
      if (multipliers.platform_cost !== undefined)
        setValue("platform_cost", multipliers.platform_cost);
    }
  };

  useEffect(() => {
    const loadFromApi = async () => {
      if (!agencyData?.id) return;
      setIsLoading(true);
      try {
        const res = await fetchController(
          baseUrl + `/api/v1/agency/${agencyData.id}`,
          "GET"
        );
        const payload = res?.data ?? res;
        const billing = payload?.billing_plan ?? payload?.billing ?? {};

        populateForm({ billing_plan: billing, subaccount_wallet: billing.subaccount_wallet });
      } catch (e) {
        if (agencyData) {
          const billing = agencyData.billing_plan || {};
          populateForm({ billing_plan: billing, subaccount_wallet: billing.subaccount_wallet });
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) loadFromApi();
  }, [isOpen, agencyData?.id, itemToUpdate]);

  const onSubmit = async (formData) => {
    try {
      if (!agencyData?.id) {
        toast.error("Agency ID missing");
        return;
      }

      setIsLoading(true);

      const multipliersPayload = {
        transcriber_cost: Number(formData.transcriber_cost) || 1,
        llm_cost: Number(formData.llm_cost) || 1,
        voice_cost: Number(formData.voice_cost) || 1,
        platform_cost: Number(formData.platform_cost) || 1,
      };

      const payload = {
        billing_plan: {
          ...agencyData?.billing_plan,
          subaccount_wallet: {
            ...(agencyData?.billing_plan?.subaccount_wallet || {}),
            multipliers: multipliersPayload,
          },
        },
      };

      const response = await fetchController(
        baseUrl + `/api/v1/agency/${agencyData.id}`,
        "PUT",
        payload
      );

      toast.success(response?.message || "Multipliers updated successfully");

      try {
        const freshRes = await fetchController(
          baseUrl + `/api/v1/agency/${agencyData.id}`,
          "GET"
        );
        const latestAgency = freshRes?.data ?? freshRes;
        if (latestAgency) {
          if (agency?.setAgencyData) agency.setAgencyData(latestAgency);
          if (onAgencyUpdated) onAgencyUpdated(latestAgency);
        }
      } catch {}

      onClose();
      reset();

      if (fetchAgencyBillingPlanList) await fetchAgencyBillingPlanList();
    } catch (error) {
      toast.error("Oops! Something went wrong while updating multipliers.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMultiplierRow = (label, fieldName, tip) => (
    <GridItem colSpan={4}>
      <FormLabelWithInfoTooltip label={label} tip={tip} />

      {/* Single Controller rendering both input and slider so they stay in sync */}
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: true, min: 1, max: 100 }}
        render={({ field: { onChange, value } }) => (
          <HStack spacing={4} align="center" mt={2}>
            <Input
              width="110px"
              type="number"
              step="0.01"
              min={1}
              max={100}
              value={value ?? 1}
              onChange={(e) => {
                const v = Number(e.target.value);
                onChange(Number.isNaN(v) ? "" : Number(v.toFixed(2)));
              }}
            />

            <Slider
              flex={1}
              min={1}
              max={100}
              step={0.01}
              value={Number(value) || 1}
              onChange={(val) => onChange(Number(val.toFixed(2)))}
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </HStack>
        )}
      />

      {errors[fieldName] && (
        <p style={{ color: "red", fontSize: "11px" }}>
          {label} is required (1 - 100)
        </p>
      )}
    </GridItem>
  );

  return (
    <Modal size="md" finalFocusRef={finalRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent height="auto" width="95%" maxWidth="700px">
        <ModalHeader color={textColor}>Configure Multipliers</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justifyContent="center"><Spinner size="lg" /></Flex>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onSubmit)(); }}>
              <Grid templateColumns="repeat(4, 1fr)" gap={6}>
                {renderMultiplierRow(
                  "Transcriber Multiplier",
                  "transcriber_cost",
                  "Multiplier applied to transcriber costs"
                )}

                {renderMultiplierRow(
                  "LLM Multiplier",
                  "llm_cost",
                  "Multiplier applied to LLM calls"
                )}

                {renderMultiplierRow(
                  "Voice Multiplier",
                  "voice_cost",
                  "Multiplier applied to voice usage"
                )}

                {renderMultiplierRow(
                  "Platform Multiplier",
                  "platform_cost",
                  "Platform overhead multiplier"
                )}
              </Grid>

              <Flex justifyContent="flex-end" mt={6}>
                <Button isLoading={isSubmitting} width="100px" colorScheme={buttonColorScheme} type="button" onClick={() => handleSubmit(onSubmit)()}>
                  Save
                </Button>
              </Flex>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ConfigureMultipliersModal;
